






































































































































































































import {
  ref,
  defineComponent,
  PropType,
  watch,
  computed,
  onMounted
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import auth from '@/mixins/auth';
import { IRootState } from '@/store';
import {
  IQuoteActions,
  IQuoteGetters,
  IQuoteMutations,
  IQuoteState
} from '@/store/quote';

import { ALForm } from '@/common/component.types';
import { IQuote, ISystemError } from '@/models';
import { percentageValidator, requiredValidator } from '@/utils/validators';
import { setErrorFromAxiosError } from '@/utils/helpers';
import { QuoteSurchargeApplicationTypeEnum } from '@/models/quote/quote.constants';

import CountryPicker from '../common/CountryPicker.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import QuoteDiscountPercentTextField from '@/components/quotes/QuoteDiscountPercentTextField.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
export default defineComponent({
  name: 'QuoteReviewingTariffSurchargeCard',

  components: {
    CountryPicker,
    ConfirmationDialog,
    ErrorAlert,
    QuoteDiscountPercentTextField
  },

  props: {
    quote: {
      type: Object as PropType<IQuote>,
      required: true
    }
  },

  mixins: [auth],

  setup(props, { emit }) {
    const error = ref<ISystemError | null>(null);

    function clearErrors() {
      error.value = null;
      surchargeDiscountError.value = null;
    }

    const isLoading = ref(false);

    const isFormValid = ref(false);

    const tariffAllItems = ref(0);
    const tariffMandatoryItems = ref(0);

    const store = useStore<IRootState>();

    const surchargeApplicationType = ref<QuoteSurchargeApplicationTypeEnum>(
      props.quote.surcharge_application_type
    );

    const surchargeDiscountPercent = ref(
      props.quote.surcharge_discount_percent
    );

    const { useActions } = createNamespacedHelpers<
      IQuoteState,
      IQuoteMutations,
      IQuoteActions,
      IQuoteGetters
    >(store, 'quote');

    const {
      applySurcharges,
      evaluateSurcharges,
      applySurchargeDiscountPercentage
    } = useActions([
      'applySurcharges',
      'evaluateSurcharges',
      'applySurchargeDiscountPercentage'
    ]);

    const { useActions: useSnackbarActions } = createNamespacedHelpers(
      store,
      'snackbar'
    );
    const { createSnack } = useSnackbarActions(['createSnack']);

    async function applyTariffSurcharge(
      tariffType: QuoteSurchargeApplicationTypeEnum
    ) {
      clearErrors();

      isLoading.value = true;

      applySurcharges({
        id: props.quote.number!,
        applicationType: tariffType
      })
        .then(() => {
          createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: 'Applied tariffs successfully',
            btnText: 'Ok',
            timeout: 6000
          });
          emit('updated:quote');

          // fetch the latest surcharge values once again
          calculateQuoteTariffs();
        })
        .catch(err => {
          error.value = setErrorFromAxiosError(err as AxiosError);
        })
        .finally(() => {
          isLoading.value = false;
        });
    }

    const formRef = ref<ALForm | null>(null);

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    onMounted(() => {
      calculateQuoteTariffs();
    });

    const isCalculatingQuoteTariff = ref(false);
    async function calculateQuoteTariffs() {
      clearErrors();

      isCalculatingQuoteTariff.value = true;
      evaluateSurcharges(props.quote.number!)
        .then(
          ({ total_surcharge_all_items, total_surcharge_mandatory_items }) => {
            tariffAllItems.value = total_surcharge_all_items;
            tariffMandatoryItems.value = total_surcharge_mandatory_items;

            emit('update:validity', !isSurchargeMismatching.value);
          }
        )
        .catch(err => {
          error.value = setErrorFromAxiosError(err);
        })
        .finally(() => {
          isCalculatingQuoteTariff.value = false;
        });
    }

    const isApplyingSurchargeDiscountLoading = ref(false);
    const isSurchargeDiscountDialogActive = ref(false);
    const surchargeDiscountError = ref<ISystemError | null>(null);

    async function save(
      surchargeType: QuoteSurchargeApplicationTypeEnum,
      discountPercent: number
    ) {
      clearErrors();
      const valid = await validateForm();
      // TODO: LH - 2025-05-05 - Improve the code by separating the form logic and the save logic.
      // Move them to different functions to enhance readability and maintainability.
      if (valid || surchargeType === QuoteSurchargeApplicationTypeEnum.None) {
        try {
          surchargeApplicationType.value = surchargeType;
          isLoading.value = true;
          isApplyingSurchargeDiscountLoading.value = true;
          await applySurchargeDiscountPercentage({
            id: props.quote.number!,
            discountPercent: discountPercent
          });
          await applyTariffSurcharge(surchargeApplicationType.value);
          isSurchargeDiscountDialogActive.value = false;
        } catch (err) {
          surchargeDiscountError.value = setErrorFromAxiosError(
            err as AxiosError
          );
        } finally {
          isApplyingSurchargeDiscountLoading.value = false;
          isLoading.value = false;
        }
      }
    }

    watch(
      () => props.quote,
      newValue => {
        if (!newValue) return;
      },
      { immediate: true, deep: true }
    );

    const { useGetters: useCurrencyGetters } = createNamespacedHelpers(
      store,
      'currency'
    );

    const { getCurrencyById } = useCurrencyGetters(['getCurrencyById']);

    const currencyCode = computed<string>(() => {
      return (
        getCurrencyById.value(props.quote.currency!)?.currency.code ?? 'USD'
      );
    });

    const isSurchargeMismatching = computed(() => {
      let isMismatching = false;

      switch (props.quote.surcharge_application_type) {
        case QuoteSurchargeApplicationTypeEnum.All:
          isMismatching = tariffAllItems.value !== props.quote.surcharge;
          break;
        case QuoteSurchargeApplicationTypeEnum.Mandatory:
          isMismatching = tariffMandatoryItems.value !== props.quote.surcharge;
          break;
        case QuoteSurchargeApplicationTypeEnum.None:
          isMismatching = props.quote.surcharge !== 0;
          break;
      }

      return isMismatching;
    });

    return {
      // refs
      formRef,
      isFormValid,

      isApplyingSurchargeDiscountLoading,
      isSurchargeDiscountDialogActive,
      surchargeDiscountError,
      // variables,
      error,
      clearErrors,
      isLoading,
      isCalculatingQuoteTariff,
      QuoteSurchargeApplicationTypeEnum,
      currencyCode,
      tariffAllItems,
      tariffMandatoryItems,
      isSurchargeMismatching,

      // tariffs
      save,

      // utils
      percentageValidator,
      requiredValidator,
      surchargeApplicationType,
      surchargeDiscountPercent
    };
  }
});
